/**
 * The master Pre-billing page (/t/:tenantSlug/invoices/prebilling): every
 * client's note lines for a date range, before they go on an invoice.
 *
 * Gate: canViewMasterPrebilling (the server's view_master_prebilling). The
 * amount column draws only for a seat holding view_invoice; the server
 * leaves the amount out for any other seat.
 *
 * A line reads as it does on the client's Pre-billing tab: the same date,
 * hours, rate (the band, with the After Hours mark beside it), invoice
 * line and Ready cell (PrebillingReadyCell), each cell but the invoice
 * line on one line, and the same "What the states mean" link (LP-18). The
 * Ready switch shows on a line the server would let this seat mark: a
 * holder of moderate_clinical_notes (the office backstop) on every line,
 * any other note writer on their own lines only. The status counts, the
 * total and the page are the server's. Any date, filter or status change
 * asks for page 1. The status and the dates are in the address
 * (`status`, `month` or `from`/`to`), so a link can open the page on them. Mark all marks the eligible lines on this page, one
 * request each, through the client tab's route. A refused mark says why on
 * a line of its own under the row. Edit opens the client tab's edit box
 * under a line for a seat that may change the note (canChangeNote: its
 * author, or a seat that moderates notes): the whole line for its author
 * and the office, the invoice line alone for any other such seat. On a phone each line is a card holding
 * the same facts.
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import {
  canChangeNote,
  canManageClientRates,
  canModerateClinicalNotes,
  canReadClinicalRecord,
  canViewInvoice,
  canViewMasterPrebilling,
  canWriteClinicalNotes,
} from '@/lib/capabilities';
import {
  MAX_RANGE_DAYS,
  PAGE_SIZE,
  prebillingAllSearch,
  rangeDays,
  readPrebillingAllView,
  useMasterPrebilling,
  type MasterPrebillingQuery,
  type MasterPrebillingRow,
  type MasterPrebillingStatus,
} from '@/lib/prebillingAll';
import {
  bandLabelsFrom,
  formatHours,
  rateBandLabel,
  patchNoteSummary,
  setNoteBillingReady,
  toNote,
  type BillingPatch,
  type PrebillingNote,
} from '@/lib/prebilling';
import { editorIsNotAuthor, notesApi, readRateBands } from '@/lib/notes';
import { formatMoney } from '@/lib/clientRates';
import { practiceTodayIso } from '@/lib/practiceDate';
import { proseDate, rowDate } from '@/lib/proseDate';
import { contactSentence, pageReadText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useIsMobile } from '@/hooks/useIsMobile';
import { BackToInvoices } from '@/components/BackToInvoices';
import { PageShell } from '@/components/primitives/PageShell';
import { PaginationFooter } from '@/components/primitives/PaginationFooter';
import { Field } from '@/components/primitives/Field';
import { DateInput } from '@/components/primitives/DateInput';
import { SelectField } from '@/components/primitives/SelectField';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import {
  BandText,
  CombinedSaveError,
  EditRow,
  PrebillingReadyCell,
  SummaryCell,
  columnHeading,
  editProblemFor,
  missingRateBandOf,
  readyHintFor,
  readyRefusalMessage,
  refusalNeedsSetup,
  saveBillingLine,
  type EditProblem,
} from '@/components/clientRecord/PreBilling';
import { removeErrorMessage } from '@/components/notes/NotesFeed';
import { bandOptionsForWhoami } from '@/lib/bandVisibility';
import { invalidateInvoiceReads } from '@/lib/invoiceInvalidation';
import { invalidateMyPrebilling } from '@/lib/myPrebilling';
import { invalidateMyHours } from '@/lib/timeEntriesApi';
import { PrebillingStatesLegend } from '@/components/clientRecord/PrebillingStatesLegend';

const RANGE_TOO_LONG = 'Choose a range of a year or less.';

/** The Amount column's note: no amount shows until a line is Ready. It is
 *  the header's tooltip, still read aloud, so the header is one line. */
export const AMOUNT_NOTE = 'Set when a line is marked Ready';

/** A refused mark, as this page says it under the note's invoice line: a
 *  missing rate names its band, and the person who can add it; a line
 *  that still needs a band or hours (`setup`) is a caution, since nothing
 *  failed; anything else (`failed`) is the server's or the house's
 *  sentence. */
export type RowProblem =
  | { kind: 'no_rate'; band: string }
  | { kind: 'setup'; text: string }
  | { kind: 'failed'; text: string };

/** "a" or "an" before a band's label. */
function article(label: string): string {
  return /^[aeiou]/i.test(label) ? 'an' : 'a';
}

/** The missing-rate line for a seat that cannot set rates: the band, and
 *  who can add it, by the billing contact. */
export function noRateAskText(bandLabel: string, contact: SupportContact): string {
  // The contact opens the second sentence ("your administrator" when none
  // is set), so it starts with a capital.
  const who = contactSentence('{contact_billing}', contact);
  const opening = who.charAt(0).toUpperCase() + who.slice(1);
  return `This note needs ${article(bandLabel)} ${bandLabel} rate before it can be billed. ${opening} can add it.`;
}

/** A refused mark, under the note's own invoice line. A missing rate
 *  reads, for a seat that sets rates, as the fix with the client's Rates
 *  one press away; for any other seat, as who can add it. Neither is a
 *  failure, so both are in the caution gold, as a line that still needs a
 *  band or hours is. Every one is announced when it appears (role alert);
 *  the colour, not the role, says how serious it is. */
function RowProblemLine({
  noteId,
  problem,
  clientName,
  ratesHref,
  canSetRates,
  bandLabels,
  contact,
}: {
  noteId: string;
  problem: RowProblem;
  clientName: string;
  ratesHref: string;
  canSetRates: boolean;
  bandLabels: Record<string, string>;
  contact: SupportContact;
}): ReactNode {
  const testId = `prebilling-ready-problem-${noteId}`;
  if (problem.kind === 'failed') {
    return (
      <p role="alert" className="mt-1 text-xs text-pepper" data-testid={testId}>
        {problem.text}
      </p>
    );
  }
  const caution = 'mt-1 text-xs text-caution-deep';
  if (problem.kind === 'setup') {
    return (
      <p role="alert" className={caution} data-testid={testId}>
        {problem.text}
      </p>
    );
  }
  const label = rateBandLabel(problem.band, bandLabels);
  return (
    <p role="alert" className={caution} data-testid={testId} data-problem="no-rate">
      {canSetRates ? (
        <>
          Add {article(label)} {label} rate to {clientName}&apos;s{' '}
          <Link
            to={ratesHref}
            className="font-semibold underline underline-offset-2"
            data-testid={`prebilling-rates-link-${noteId}`}
          >
            Rates
          </Link>{' '}
          to bill this note.
        </>
      ) : (
        noRateAskText(label, contact)
      )}
    </p>
  );
}

const STATUSES: ReadonlyArray<{
  value: MasterPrebillingStatus;
  label: string;
  count: 'all' | 'ready' | 'not_ready';
}> = [
  { value: 'all', label: 'All', count: 'all' },
  { value: 'ready', label: 'Ready', count: 'ready' },
  { value: 'not_ready', label: 'Not ready', count: 'not_ready' },
];

/** A line the server would let this seat mark or unmark. */
function eligible(r: MasterPrebillingRow): boolean {
  return r.readiness === 'needs_your_ok' || r.readiness === 'ready';
}

export function PrebillingAll(): ReactNode {
  const { whoami, tenantSlug = '' } = useTenant();
  const contact = useSupportContact();
  const queryClient = useQueryClient();
  const phone = useIsMobile();
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const bands = readRateBands(tenantConfig?.config);
  const bandLabels = bandLabelsFrom(bands);
  const allowed = canViewMasterPrebilling(whoami);
  const invoiceSeat = canViewInvoice(whoami);
  const recordSeat = canReadClinicalRecord(whoami);
  // Which missing-rate line a seat reads: the fix with a link to the
  // client's Rates for a seat that sets rates and can open the client's
  // billing page (view_invoice gates it), who can add it otherwise.
  const ratesSeat = canManageClientRates(whoami) && invoiceSeat;
  const writer = canWriteClinicalNotes(whoami);
  const backstop = writer && canModerateClinicalNotes(whoami);

  // The status and the dates live in the address too, so another page can
  // link here (Invoices: `?status=not_ready&month=YYYY-MM`) and a reload
  // keeps them. The boxes hold what is typed; a valid view is written back
  // to the address, replacing the entry (no reload, no extra history).
  const today = useMemo(() => practiceTodayIso(), []);
  const [params, setParams] = useSearchParams();
  const [opening] = useState(() => readPrebillingAllView(params, today));
  const [from, setFrom] = useState(opening.from);
  const [to, setTo] = useState(opening.to);
  const [status, setStatus] = useState<MasterPrebillingStatus>(opening.status);
  const [author, setAuthor] = useState('');
  const [leadCm, setLeadCm] = useState('');
  const [client, setClient] = useState('');
  const [band, setBand] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Per line: the value a request in flight is setting, and a refusal.
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [problems, setProblems] = useState<Record<string, RowProblem>>({});

  const span = rangeDays(from, to);
  const rangeProblem =
    span === null || span < 1
      ? 'Choose a start and an end date.'
      : span > MAX_RANGE_DAYS
        ? RANGE_TOO_LONG
        : null;
  // The address this page last wrote, and the latest address: an address
  // that changes from elsewhere (a link here while the page is open) is
  // read into the view; a change to the view is written to the address.
  const written = useRef(params.toString());
  const latest = useRef(params);
  latest.current = params;
  useEffect(() => {
    const now = params.toString();
    if (now === written.current) return;
    written.current = now;
    const view = readPrebillingAllView(params, today);
    setFrom(view.from);
    setTo(view.to);
    setStatus(view.status);
    setPage(1);
  }, [params, today]);
  useEffect(() => {
    if (rangeProblem !== null) return;
    const next = prebillingAllSearch(latest.current, { from, to, status }, today);
    if (next.toString() === latest.current.toString()) return;
    written.current = next.toString();
    setParams(next, { replace: true });
  }, [from, to, status, rangeProblem, setParams, today]);

  // A range inside one year reads 'Oct 3'; one that crosses a year names it.
  const withYear = from.slice(0, 4) !== to.slice(0, 4);

  const query: MasterPrebillingQuery = {
    from,
    to,
    status,
    author,
    lead_cm: leadCm,
    client,
    band,
    page,
    page_size: PAGE_SIZE,
  };
  const read = useMasterPrebilling(tenantSlug, query, allowed && rangeProblem === null);
  const data = read.data;

  /** Every change to what the list shows starts again at page 1, and
   *  drops the refusals the old list showed. */
  function change(fn: () => void): void {
    fn();
    setPage(1);
    setProblems({});
  }
  /** A note changed by Edit or removed: its refusal no longer holds. */
  function forgetProblem(noteId: string): void {
    setProblems((p) => {
      const { [noteId]: _gone, ...rest } = p;
      return rest;
    });
  }

  function canMark(r: MasterPrebillingRow): boolean {
    if (!writer || !eligible(r)) return false;
    return backstop || r.author_user_id === whoami?.user_id;
  }

  async function mark(noteIds: string[], ready: boolean): Promise<void> {
    for (const id of noteIds) {
      setPending((p) => ({ ...p, [id]: ready }));
      setProblems((p) => {
        const { [id]: _gone, ...rest } = p;
        return rest;
      });
      try {
        await setNoteBillingReady(tenantSlug, id, ready);
      } catch (err) {
        const band = missingRateBandOf(err);
        const problem: RowProblem =
          band !== null
            ? { kind: 'no_rate', band }
            : {
                kind: refusalNeedsSetup(err) ? 'setup' : 'failed',
                text: readyRefusalMessage(err, invoiceSeat, contact, 'all'),
              };
        setProblems((p) => ({ ...p, [id]: problem }));
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['master-prebilling', tenantSlug] });
    setPending((p) => {
      const rest = { ...p };
      for (const id of noteIds) delete rest[id];
      return rest;
    });
  }

  const rows = data?.rows ?? [];
  const nativeFrom = data?.native_from ?? null;
  const markable = rows.filter((r) => r.readiness === 'needs_your_ok' && canMark(r));

  // Edit: the client tab's box, one line at a time. Its bands are the ones
  // this seat may pick, by label; a seat without view_invoice sees no price
  // anywhere in it.
  const allowedBands = bandOptionsForWhoami(whoami, tenantConfig?.config, bands);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editProblem, setEditProblem] = useState<({ noteId: string } & EditProblem) | null>(null);
  const [removeProblem, setRemoveProblem] = useState<{ noteId: string; message: string } | null>(null);
  // A box closed by Cancel or a save hands focus back to its Edit.
  const returnFocusTo = useRef<string | null>(null);
  useEffect(() => {
    if (editingRow !== null || returnFocusTo.current === null) return;
    document.querySelector<HTMLButtonElement>(`[data-testid="prebilling-edit-${returnFocusTo.current}"]`)?.focus();
    returnFocusTo.current = null;
  }, [editingRow]);
  function mayChange(r: MasterPrebillingRow): boolean {
    return writer && canChangeNote(whoami, r.author_user_id) && r.readiness !== 'still_reading' && r.readiness !== 'failed';
  }
  /** A change here moves this list, the client's tab, the invoice pages,
   *  My time and the hours. */
  function refreshAfterEdit(): void {
    void queryClient.invalidateQueries({ queryKey: ['master-prebilling', tenantSlug] });
    void queryClient.invalidateQueries({ queryKey: ['prebilling', tenantSlug] });
    invalidateInvoiceReads(queryClient, tenantSlug);
    void invalidateMyHours(queryClient, tenantSlug);
    void invalidateMyPrebilling(queryClient, tenantSlug);
  }
  function closeBox(noteId: string): void {
    returnFocusTo.current = noteId;
    setEditProblem(null);
    setRemoveProblem(null);
    setEditingRow(null);
  }
  const saveMut = useMutation<unknown, unknown, { noteId: string; patch: BillingPatch; summary?: string }>({
    mutationFn: ({ noteId, patch, summary }) => saveBillingLine(tenantSlug, noteId, patch, summary),
    onMutate: () => setEditProblem(null),
    onSuccess: (_res, { noteId }) => {
      closeBox(noteId);
      forgetProblem(noteId);
      refreshAfterEdit();
    },
    onError: (err, { noteId }) => {
      setEditProblem({ noteId, ...editProblemFor(err, contact) });
      if (err instanceof CombinedSaveError && err.lineSaved) refreshAfterEdit();
    },
  });
  const lineMut = useMutation<unknown, unknown, { noteId: string; summary: string }>({
    mutationFn: async ({ noteId, summary }) => {
      try {
        await patchNoteSummary(tenantSlug, noteId, summary);
      } catch (err) {
        throw new CombinedSaveError('line', false, err);
      }
      return null;
    },
    onMutate: () => setEditProblem(null),
    onSuccess: (_res, { noteId }) => {
      closeBox(noteId);
      forgetProblem(noteId);
      refreshAfterEdit();
    },
    onError: (err, { noteId }) => setEditProblem({ noteId, ...editProblemFor(err, contact) }),
  });
  const removeMut = useMutation<unknown, unknown, { noteId: string }>({
    mutationFn: ({ noteId }) => notesApi.removeNote(tenantSlug, noteId),
    onMutate: () => setRemoveProblem(null),
    onSuccess: (_res, { noteId }) => {
      setEditProblem(null);
      setEditingRow(null);
      forgetProblem(noteId);
      refreshAfterEdit();
      void queryClient.invalidateQueries({ queryKey: ['kith-notes', tenantSlug] });
    },
    onError: (err, { noteId }) => setRemoveProblem({ noteId, message: removeErrorMessage(err, contact) }),
  });

  function editButton(r: MasterPrebillingRow): ReactNode {
    if (!mayChange(r) || editingRow === r.note_id) return null;
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setEditProblem(null);
          setRemoveProblem(null);
          setEditingRow(r.note_id);
        }}
        data-testid={`prebilling-edit-${r.note_id}`}
      >
        Edit
      </Button>
    );
  }

  function editBox(r: MasterPrebillingRow, note: PrebillingNote, layout: 'table' | 'card'): ReactNode {
    if (editingRow !== r.note_id) return null;
    const own = r.author_user_id === whoami?.user_id;
    return (
      <EditRow
        note={note}
        bands={bands}
        allowedBands={allowedBands}
        lineOnly={!invoiceSeat && !own}
        columnCount={columnCount}
        readyHint={readyHintFor(note, invoiceSeat, own)}
        notAuthor={editorIsNotAuthor(whoami?.user_id ?? null, r.author_user_id)}
        problem={editProblem?.noteId === r.note_id ? { message: editProblem.message, date: editProblem.date } : null}
        onDateChanged={() => setEditProblem((p) => (p === null || p.date === null ? p : { ...p, date: null }))}
        onSave={(patch, summary) => saveMut.mutate({ noteId: r.note_id, patch, summary })}
        onSaveLine={(summary) => lineMut.mutate({ noteId: r.note_id, summary })}
        onCancel={() => closeBox(r.note_id)}
        saving={saveMut.isPending || lineMut.isPending}
        canRemove={canChangeNote(whoami, r.author_user_id)}
        onRemove={() => removeMut.mutate({ noteId: r.note_id })}
        removing={removeMut.isPending}
        removeProblem={removeProblem?.noteId === r.note_id ? removeProblem.message : null}
        layout={layout}
      />
    );
  }
  const marking = Object.keys(pending).length > 0;

  function amountCell(r: MasterPrebillingRow): ReactNode {
    // An en dash (the house bans the em dash) until the line is Ready.
    if (r.readiness !== 'ready') {
      return (
        <span className="text-ink-soft">
          <span aria-hidden="true">–</span>
          <span className="sr-only">Not set yet</span>
        </span>
      );
    }
    if (r.amount !== null && r.amount !== undefined) return formatMoney(r.amount);
    return (
      <span className="text-ink-soft">
        {nativeFrom === null || r.occurred_at < nativeFrom
          ? 'Amount when billing starts'
          : 'No amount yet'}
      </span>
    );
  }

  function clientHref(r: MasterPrebillingRow): string {
    return recordSeat
      ? `/t/${tenantSlug}/clients/${r.client_id}?tab=pre-billing&month=${r.occurred_at.slice(0, 7)}`
      : `/t/${tenantSlug}/invoices/clients/${r.client_id}`;
  }

  /** The client's name and its Demo chip. In the table (`oneLine`) they
   *  stay on one line, the chip inline after the name: the Invoice line
   *  column takes the width that is left. The stacked cards may wrap. */
  function clientName(r: MasterPrebillingRow, oneLine = false): ReactNode {
    return (
      <span
        className={`inline-flex items-center gap-1.5 ${oneLine ? 'flex-nowrap whitespace-nowrap' : 'flex-wrap'}`}
        data-testid="prebilling-all-client-name"
      >
        <Link to={clientHref(r)} className="font-medium underline-offset-2 hover:underline">
          {r.client_display_name}
        </Link>
        {r.client_is_demo && (
          <Chip variant="neutral" data-testid="prebilling-all-demo">
            Demo
          </Chip>
        )}
      </span>
    );
  }

  function hoursText(note: PrebillingNote): ReactNode {
    return note.hoursEntered ? (
      formatHours(note.hours)
    ) : (
      <span className="italic text-ink-soft">No hours</span>
    );
  }

  /** The Ready cell. In a card the state words are on a kept line under
   *  the switch; the table, with eight columns to fit, makes them the
   *  switch line's tooltip (`inTable`). A refusal is never here: it sits
   *  under the note's invoice line (problemLine), in the table and in a
   *  card, so it reads as part of that note. */
  function readyCell(r: MasterPrebillingRow, note: PrebillingNote, inTable = false): ReactNode {
    return (
      <PrebillingReadyCell
        note={note}
        canMark={canMark(r)}
        viewerIsAuthor={r.author_user_id === whoami?.user_id}
        readyPending={pending[r.note_id]}
        onSetReady={(v) => void mark([r.note_id], v)}
        readyProblem={null}
        problemBelow={false}
        stateWords={inTable ? 'tooltip' : 'below'}
        switchName={`Ready to bill, ${r.client_display_name}, ${proseDate(r.occurred_at)}`}
      />
    );
  }
  function problemLine(r: MasterPrebillingRow): ReactNode {
    const problem = problems[r.note_id];
    if (problem === undefined) return null;
    return (
      <RowProblemLine
        noteId={r.note_id}
        problem={problem}
        clientName={r.client_display_name}
        ratesHref={`/t/${tenantSlug}/invoices/clients/${r.client_id}`}
        canSetRates={ratesSeat}
        bandLabels={bandLabels}
        contact={contact}
      />
    );
  }
  // Edit is the last column for a seat that writes notes.
  const columnCount = (invoiceSeat ? 8 : 7) + (writer ? 1 : 0);

  // Every column but the invoice line is as narrow as its content
  // (`w-px` under the table's auto layout); the invoice line takes the
  // width that is left.
  const th = 'w-px py-1.5 pr-3 font-semibold whitespace-nowrap';

  return (
    <PageShell
      title="Pre-billing"
      maxWidth="wide"
      testId="prebilling-all-page"
      access={{
        allowed,
        deniedMessage:
          "Pre-billing for every client is for the office. Each client's own Pre-billing is on their client record.",
      }}
    >
      <BackToInvoices tenantSlug={tenantSlug} />
      <div className="flex flex-wrap items-start gap-3">
        <Field label="From" htmlFor="prebilling-all-from" error={rangeProblem ?? undefined}>
          <DateInput
            id="prebilling-all-from"
            data-testid="prebilling-all-from"
            value={from}
            onChange={(e) => change(() => setFrom(e.target.value))}
          />
        </Field>
        <Field label="To" htmlFor="prebilling-all-to">
          <DateInput
            id="prebilling-all-to"
            data-testid="prebilling-all-to"
            value={to}
            onChange={(e) => change(() => setTo(e.target.value))}
          />
        </Field>
        <Field label="Written by" htmlFor="prebilling-all-author">
          <SelectField
            id="prebilling-all-author"
            data-testid="prebilling-all-author"
            value={author}
            onChange={(e) => change(() => setAuthor(e.target.value))}
          >
            <option value="">Everyone</option>
            {(data?.options.authors ?? []).map((a) => (
              <option key={a.user_id} value={a.user_id}>
                {a.display_name}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field label="Client's care manager" htmlFor="prebilling-all-lead-cm">
          <SelectField
            id="prebilling-all-lead-cm"
            data-testid="prebilling-all-lead-cm"
            value={leadCm}
            onChange={(e) => change(() => setLeadCm(e.target.value))}
          >
            <option value="">Everyone</option>
            {(data?.options.lead_cms ?? []).map((l) => (
              <option key={l.roster_id} value={l.roster_id}>
                {l.name}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field label="Client" htmlFor="prebilling-all-client">
          <SelectField
            id="prebilling-all-client"
            data-testid="prebilling-all-client"
            value={client}
            onChange={(e) => change(() => setClient(e.target.value))}
          >
            <option value="">All clients</option>
            {(data?.options.clients ?? []).map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.display_name}
              </option>
            ))}
          </SelectField>
        </Field>
        <Field label="Rate band" htmlFor="prebilling-all-band">
          <SelectField
            id="prebilling-all-band"
            data-testid="prebilling-all-band"
            value={band === null ? '*' : band === '' ? '-' : band}
            onChange={(e) =>
              change(() =>
                setBand(
                  e.target.value === '*' ? null : e.target.value === '-' ? '' : e.target.value,
                ),
              )
            }
          >
            <option value="*">All rate bands</option>
            <option value="-">No rate band</option>
            {bands.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </SelectField>
        </Field>
      </div>

      {data !== undefined && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Status">
            {STATUSES.map((s) => (
              <Button
                key={s.value}
                variant={status === s.value ? 'primary' : 'ghost'}
                size="sm"
                aria-pressed={status === s.value}
                data-testid={`prebilling-all-status-${s.value}`}
                onClick={() => change(() => setStatus(s.value))}
              >
                {s.label} ({data.counts[s.count]})
              </Button>
            ))}
          </div>
          <PrebillingStatesLegend officeSeat={invoiceSeat} />
          {writer && markable.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto"
              data-testid="prebilling-all-mark-all"
              disabled={marking}
              loading={marking}
              onClick={() =>
                void mark(
                  markable.map((r) => r.note_id),
                  true,
                )
              }
            >
              Mark {markable.length} ready
            </Button>
          )}
        </div>
      )}

      {data !== undefined && data.drafts.length > 0 && (
        <section
          data-testid="prebilling-all-drafts"
          className="mt-4 rounded-card border border-sage-800/[8%] bg-paper p-4"
        >
          <p className="text-sm text-ink">
            Notes still in draft in these dates. They won&apos;t bill until they&apos;re posted.
          </p>
          <ul className="mt-2 flex flex-col gap-1 text-sm">
            {data.drafts.map((d) => (
              <li key={d.client_id} data-testid="prebilling-all-draft-client">
                <span className="font-medium">{d.client_display_name}</span>: {d.count}{' '}
                {d.count === 1 ? 'unposted draft' : 'unposted drafts'} ·{' '}
                {d.authors
                  .map((a) =>
                    d.authors.length === 1 ? a.display_name : `${a.display_name} (${a.count})`,
                  )
                  .join(', ')}
              </li>
            ))}
          </ul>
        </section>
      )}

      {read.isError && (
        <p role="alert" className="mt-4 text-sm text-pepper">
          {pageReadText(read.error, contact, 'the pre-billing lines')}
        </p>
      )}

      {data !== undefined && (
        <>
          {rows.length === 0 ? (
            <p className="mt-6 text-sm text-ink-soft">
              {author || leadCm || client || band !== null || status !== 'all'
                ? 'Clear a filter to widen the list.'
                : "Once notes post for these dates, you'll see them here."}
            </p>
          ) : phone ? (
            // A phone: one card per line, the same facts in the same words.
            <ul className="mt-4 flex flex-col gap-3" data-testid="prebilling-all-cards">
              {rows.map((r) => {
                const note = toNote(r, nativeFrom);
                return (
                  <li
                    key={r.note_id}
                    data-testid="prebilling-all-row"
                    data-note-id={r.note_id}
                    className="flex min-w-0 flex-col gap-2 rounded-card border border-sage-800/[8%] bg-paper p-3 text-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      {clientName(r)}
                      <span className="whitespace-nowrap text-sage-800">
                        {rowDate(r.occurred_at, withYear)}
                      </span>
                    </div>
                    <div className="text-sage-800">
                      {hoursText(note)} · <BandText note={note} bandLabels={bandLabels} />
                    </div>
                    <div className="text-ink-soft">{r.author_display_name ?? ''}</div>
                    <div className="text-[0.78125rem] leading-relaxed break-words">
                      <SummaryCell note={note} />
                      {problemLine(r)}
                    </div>
                    {readyCell(r, note)}
                    {invoiceSeat && (
                      <div data-testid="prebilling-all-amount">
                        <span className="text-ink-soft">Amount: </span>
                        {amountCell(r)}
                      </div>
                    )}
                    {editButton(r) !== null && <div>{editButton(r)}</div>}
                    {editBox(r, note, 'card')}
                  </li>
                );
              })}
            </ul>
          ) : (
            // Positioned, so the switch line's screen-reader-only words are
            // clipped with the table and never widen the page.
            // It is an inline-size container too: an open edit box is as wide
            // as its visible area and sticks to its left edge.
            <div className="relative mt-4 overflow-x-auto @container" data-testid="prebilling-all-table-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left align-bottom text-label font-semibold uppercase tracking-[0.12em] text-sage-400">
                    <th className={th}>Client</th>
                    <th className={th}>Date</th>
                    <th className={th}>Written by</th>
                    <th className={th}>Rate</th>
                    <th className={th}>Hours</th>
                    {/* The invoice line takes the width the others leave. */}
                    <th className="w-full py-1.5 pr-3 font-semibold" data-testid="prebilling-all-line-header">
                      Invoice line
                    </th>
                    <th className={th}>Status</th>
                    {invoiceSeat && (
                      // One line: when the amount is set is the header's
                      // tooltip, and is read aloud.
                      <th className={th} title={AMOUNT_NOTE} data-testid="prebilling-all-amount-header">
                        <span className="underline decoration-dotted underline-offset-4">Amount</span>
                        <span className="sr-only" data-testid="prebilling-all-amount-note">
                          {AMOUNT_NOTE}
                        </span>
                      </th>
                    )}
                    {writer && <th className="w-px py-1.5 font-semibold">{columnHeading('Edit')}</th>}
                  </tr>
                </thead>
                {/* One body per note: the note's row, and its edit box when
                    open, sit together, with a clear line between notes. */}
                {rows.map((r) => {
                    const note = toNote(r, nativeFrom);
                    return (
                      <tbody
                        key={r.note_id}
                        className="border-t border-sage-200 first-of-type:border-t-0"
                        data-testid={`prebilling-all-note-${r.note_id}`}
                      >
                      <tr
                        data-testid="prebilling-all-row"
                        data-note-id={r.note_id}
                        className="align-top"
                      >
                        <td className="py-2 pr-3 whitespace-nowrap">{clientName(r, true)}</td>
                        <td className="whitespace-nowrap py-2 pr-3 text-sage-800">
                          {rowDate(r.occurred_at, withYear)}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-3">
                          {r.author_display_name ?? ''}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-3 text-sage-800">
                          <BandText note={note} bandLabels={bandLabels} />
                        </td>
                        <td className="whitespace-nowrap py-2 pr-3 text-sage-800">
                          {hoursText(note)}
                        </td>
                        {/* 14rem at the least: room for Edit at 1280 beside the
                            other columns, which keep to one line. */}
                        <td className="min-w-[14rem] py-2 pr-3 text-[0.78125rem] leading-relaxed">
                          <SummaryCell note={note} />
                          {problemLine(r)}
                        </td>
                        <td className="py-2 pr-3">
                          {/* The line keeps its width: the switch and its
                              state words never wrap under each other. */}
                          <div className="min-w-max">{readyCell(r, note, true)}</div>
                        </td>
                        {invoiceSeat && (
                          <td className="py-2 pr-3" data-testid="prebilling-all-amount">
                            {/* An amount stays on one line; the words for a
                                line with none yet wrap at 6rem, so the
                                column stays tight. */}
                            <div className="w-max max-w-[6rem]">{amountCell(r)}</div>
                          </td>
                        )}
                        {writer && (
                          <td className="py-2" data-testid={`prebilling-edit-cell-${r.note_id}`}>
                            {editButton(r)}
                          </td>
                        )}
                      </tr>
                      {editBox(r, note, 'table')}
                      </tbody>
                    );
                  })}
              </table>
            </div>
          )}
          <PaginationFooter
            offset={(page - 1) * PAGE_SIZE}
            pageSize={PAGE_SIZE}
            total={data.total}
            onOffsetChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
            testIdPrefix="prebilling-all-"
          />
        </>
      )}
    </PageShell>
  );
}
